import { Check, Clock3 } from "lucide-react";
import iconImg from "@/assets/icon.png";
import { OnboardingBrand } from "./OnboardingBrand";

interface OnboardingIntroProps {
  onStart: () => void;
  onLogout?: () => void;
  iconUrl?: string | null;
  returning?: boolean;
}

const BENEFITS = ["Your player profile", "Personalized school matches", "Clear next steps"];

export default function OnboardingIntro({
  onStart,
  onLogout,
  iconUrl,
  returning = false,
}: OnboardingIntroProps) {
  const coachIcon = iconUrl || iconImg;

  return (
    <div className="relative h-dvh overflow-x-hidden overflow-y-auto overscroll-y-contain bg-forest text-white">
      <div className="relative flex min-h-full flex-col overflow-hidden">
        <div className="pointer-events-none absolute -bottom-[28vw] -right-[8vw] h-[62vw] w-[62vw] rounded-full border border-primary/20 shadow-[0_0_0_8vw_rgba(166,226,78,0.035),0_0_0_16vw_rgba(166,226,78,0.02)]" />

        <header className="relative z-10 flex min-h-[76px] items-center justify-between px-5 py-4 sm:px-8">
          <OnboardingBrand surface="dark" />
          {onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="text-xs font-bold text-sage transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              Log out
            </button>
          )}
        </header>

        <main className="relative z-10 mx-auto grid w-full max-w-6xl flex-1 grid-cols-1 items-center gap-8 px-5 pb-14 pt-4 sm:px-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-16 lg:py-10">
          <section className="order-2 lg:order-1">
            <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
              {returning
                ? "A better match starts with better context"
                : "Your college golf recruiting coach"}
            </p>
            <h1 className="max-w-[720px] text-[clamp(3rem,6vw,4.75rem)] leading-[1.02] tracking-[-0.045em] text-white">
              {returning
                ? "We’ve improved how golfrecruit.ai matches you with schools."
                : "Let's build a plan that fits you."}
            </h1>
            <p className="mt-5 max-w-[640px] text-base leading-relaxed text-sage sm:text-lg">
              {returning
                ? "We now use more of your golf data, academics, college preferences, and recruiting progress to build your list. Please review the updated questions. We’ve filled in everything we already know, and your current schools, notes, and stages will stay exactly where they are."
                : "I’ll ask about your golf, academics, and goals—then build your first balanced school list."}
            </p>

            <div className="my-7 flex flex-wrap gap-2.5">
              {BENEFITS.map(benefit => (
                <span
                  key={benefit}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-2 text-xs font-semibold text-sage"
                >
                  <Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} />
                  {benefit}
                </span>
              ))}
            </div>

            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={onStart}
                className="min-h-14 w-full rounded-2xl bg-primary px-7 text-sm font-extrabold text-forest shadow-[0_5px_0_#3c7c24] transition hover:-translate-y-0.5 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber/70 sm:w-auto"
              >
                {returning ? "Review my recruiting profile" : "Build my recruiting plan"}
              </button>
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-sage">
                <Clock3 className="h-4 w-4" /> About 5 minutes ·{" "}
                {returning ? "your existing answers are already filled in" : "saves as you go"}
              </span>
            </div>
          </section>

          <section className="relative order-1 mx-auto min-h-[290px] w-full max-w-[370px] self-end lg:order-2 lg:min-h-[510px]">
            <div className="absolute left-0 top-2 w-[190px] -rotate-3 rounded-2xl border border-forest/10 bg-white text-forest shadow-[0_22px_60px_rgba(0,0,0,0.28)] sm:w-[225px] lg:-left-14 lg:top-14">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <strong className="text-xs">Your game plan</strong>
                <span className="font-mono text-[11px] text-primary-text">0 / 6</span>
              </div>
              {[
                ["01", "Player profile", "Tell me about your game"],
                ["02", "School fit", "Find the right programs"],
                ["03", "Next steps", "Know what to do first"],
              ].map(([number, title, description]) => (
                <div
                  key={number}
                  className="grid grid-cols-[28px_1fr_18px] items-center gap-2 border-b border-border px-4 py-3 last:border-b-0"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-lime-soft text-[11px] font-extrabold text-primary-text">
                    {number}
                  </span>
                  <span>
                    <strong className="block text-[11px]">{title}</strong>
                    <small className="mt-0.5 block text-[11px] text-muted-foreground">
                      {description}
                    </small>
                  </span>
                  <span className="text-primary-text">○</span>
                </div>
              ))}
            </div>
            <div className="absolute bottom-0 left-1/2 aspect-square w-[250px] -translate-x-1/2 overflow-hidden rounded-full border-8 border-white/10 bg-black shadow-[0_35px_55px_rgba(0,0,0,0.24)] sm:w-[285px] lg:left-auto lg:right-0 lg:w-[390px] lg:translate-x-0">
              <img
                src={coachIcon}
                alt="golfrecruit.ai coach"
                className="h-full w-full object-cover"
              />
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
